import type { AreaServer } from "./servers/AreaServer.js";
import { GameFileConfig, PreConfigData } from "@gotti/core";
import { invokeOrReturn } from "@gotti/core";
import type { ServerAssetsFacade } from "./assets/assetsFacade.js";
import type { AuthIdentityPath } from "./authIdentity.js";
import type { ReconnectPolicy } from "./ServerAreaFile.js";

/**
 * Client data passed to game join resolution.
 */
export type JoinRequestClientData<Auth=any, JoinOpts=any> = {
    id: string,
    auth: Auth,
    options: JoinOpts,
}

/**
 * API passed to game file hooks.
 */
export type GameFileApi = {
    readonly meta: any,
    requestArea: (areaId: string, message: { type: string, data: any, to: number | string }) => Promise<any>,
}


/**
 * Area id with join options.
 */
export type AreaIdAndOptions = { areaId: string | number, options: any, key?: string };
/**
 * Valid area id return types.
 */
export type AreaIdTypes = AreaIdAndOptions[] | string[] | number[] | string | number | (string | number | AreaIdAndOptions)[] | AreaIdAndOptions

//TODO: it's be a good idea so we differentiate data with a state object, state will be local to each game instance, while data will be static across all games with the same type
/**
 * Server-side game definition.
 *
 * @template TApi - Shape of the object returned by the `api` method.
 *   When provided, the same keys **must** be declared in
 *   `ServerApiRegistry` via `declare module '@gotti/server'`.
 */
export type ServerGameFile<TApi = {}, Auth = any> = GameFileConfig & {
    /**
     * Dot-path to the durable identity inside this game's server-verified auth
     * (e.g. `'attributes.accountId'`) — the value an automatic rejoin is matched
     * on. REQUIRED when any of this game's areas enable `reconnect`; Starter
     * throws at config-validation time otherwise. Typed against `Auth`, so a
     * typo or renamed field is a compile error for any game that types its auth.
     */
    authIdentityPath?: AuthIdentityPath<Auth>,
    /** Default reconnect policy for this game's areas; an area file overrides it. */
    reconnect?: ReconnectPolicy,
    // if a player can join more than 1 area at a time.
    preventMultiClientAreas?: boolean,
    // Simulation timestep (in seconds). If provided, overrides tickRate.
    timeStep?: number,
    // If true, the server uses a fixed timestep update (deterministic stepping).
    // If false/omitted, server uses variable timestep (current behavior).
    fixedTimeStep?: boolean,
    meta?: any | ((gameConfig: PreConfigData) => Promise<any>)
    api?: TApi | ((data?: any, envConfigs?: any) => TApi | Promise<TApi>),
    getInitialAreas(client: JoinRequestClientData , api: GameFileApi, areaIds: Array<string>) : Promise<AreaIdTypes> | AreaIdTypes,
    onBeforeUpdate?: (server: AreaServer, delta: number) => void,
    onAfterUpdate?: (server: AreaServer, delta: number) => void,
    /** Asset paths preloaded (into $assets) before this file's systems start. */
    preloads?: { client?: string[]; server?: string[] },
    data?: {
        // processes will get copy of this data and accessible through system.$gameData 
        server?: any | ((gameConfig: PreConfigData) => Promise<any> | any), 
        client?: any | ((gameConfig: PreConfigData) => Promise<any> | any),
        shared?: any | ((gameConfig: PreConfigData) => Promise<any> | any),
    } | ((gameConfig: PreConfigData) => Promise<{ server?: any, client?: any }> | { server?: any, client?: any }),
    /*
    state?: {
        // processes will get copy of this data and accessible through system.$gameState 
        server?: JSONEncodable | ((gameConfig: PreConfigData) => Promise<JSONEncodable> | JSONEncodable), 
        client?: JSONEncodable | ((gameConfig: PreConfigData) => Promise<JSONEncodable> | JSONEncodable),
        shared?: JSONEncodable | ((gameConfig: PreConfigData) => Promise<JSONEncodable> | JSONEncodable),
    } | ((gameConfig: PreConfigData) => Promise<{ server?: JSONEncodable, client?: JSONEncodable }> | { server?: JSONEncodable, client?: JSONEncodable }),
    */
    
}

/**
 * Compute serialized game file data for client/server contexts.
 */
export async function makeGameFileData(gameFile: ServerGameFile<any>, gameId: string | number, configData?: any, skipServer?: boolean, $assets?: ServerAssetsFacade) : Promise<{ server: any, client: any }> {
    // Context handed to the data factory(ies). `$assets` (the read facade over
    // this process's preloaded asset cache) lets a factory read preloaded assets
    // synchronously. Built as a variable — not an inline literal at each call —
    // so the extra `$assets` prop doesn't trip TS excess-property checks against
    // the PreConfigData-typed factory param. Backward compatible: when `$assets`
    // is omitted, factories see `ctx.$assets === undefined` and ignore it.
    const ctx = { id: gameId, type: gameFile.type, configData, $assets };
    if(typeof gameFile?.data === 'function') {
        const { client, server } = await gameFile.data(ctx);
        return { client: client || {}, server: server || {} };
    } else {
        const sharedGameData = await invokeOrReturn(gameFile?.data?.shared || {}, ctx);
        const client = {
            ...sharedGameData,
            ...(await invokeOrReturn(gameFile?.data?.client || {}, ctx))
        };
        const server = !skipServer ? {
            ...sharedGameData,
            ...(await invokeOrReturn(gameFile?.data?.server || {}, ctx))
        } : {};
        return { client, server };
    }
}

/**
 * Compute game metadata for gate listings.
 */
export async function makeGameFileMetaData(gameFile: ServerGameFile<any>, id: string | number, configData: any) : Promise<any> {
    if(typeof gameFile?.meta === 'function') {
        return gameFile.meta({ id: id, type: gameFile.type, configData });
    } else {
        return gameFile.meta;
    }
}